<template>
  <div class="verification-code" @click="focusInput">
    <!-- 隐藏的输入框 -->
    <input
      ref="hiddenInput"
      class="verification-code__hidden-input"
      type="number"
      :maxlength="length"
      v-model="inputValue"
      @input="handleInput"
      @focus="handleFocus"
      @blur="handleBlur"
      :disabled="disabled"
    />

    <!-- 显示的验证码框 -->
    <div class="verification-code__container">
      <div
        v-for="(item, index) in codeArray"
        :key="index"
        :class="[
          'verification-code__item',
          {
            'verification-code__item--active':
              isFocused && index === currentIndex,
            'verification-code__item--filled': item !== '',
            'verification-code__item--error': hasError
          }
        ]"
        :style="itemStyle"
      >
        <span v-if="!isPassword" class="verification-code__text">
          {{ item }}
        </span>
        <span v-else class="verification-code__text">
          {{ item ? '●' : '' }}
        </span>

        <!-- 光标 -->
        <div
          v-if="isFocused && index === currentIndex && !item"
          class="verification-code__cursor"
        ></div>
      </div>
    </div>

    <!-- 错误提示 -->
    <div v-if="hasError && errorText" class="verification-code__error">
      <span>{{ errorText }}</span>
    </div>
  </div>
</template>

<script>
export default {
  name: 'VerificationCode',
  props: {
    // 验证码长度
    length: {
      type: Number,
      default: 4
    },
    // 是否密码模式（显示圆点）
    isPassword: {
      type: Boolean,
      default: false
    },
    // 是否禁用
    disabled: {
      type: Boolean,
      default: false
    },
    // 是否自动聚焦
    autoFocus: {
      type: Boolean,
      default: true
    },
    // 输入框样式
    itemStyle: {
      type: Object,
      default: () => ({})
    },
    // 错误状态
    hasError: {
      type: Boolean,
      default: false
    },
    // 错误提示文本
    errorText: {
      type: String,
      default: ''
    },
    // 输入完成后是否自动失去焦点
    autoBlur: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      inputValue: '',
      isFocused: false,
      codeArray: new Array(this.length).fill('')
    };
  },
  computed: {
    currentIndex() {
      return this.inputValue.length;
    },
    isComplete() {
      return this.inputValue.length === this.length;
    }
  },
  watch: {
    inputValue: {
      handler(newVal) {
        this.updateCodeArray(newVal);
        this.$emit('input', newVal);
        this.$emit('change', newVal);

        // 输入完成
        if (newVal.length === this.length) {
          this.$emit('complete', newVal);
          if (this.autoBlur) {
            this.$nextTick(() => {
              this.blur();
            });
          }
        }
      },
      immediate: true
    },
    hasError: {
      handler(newVal) {
        if (newVal) {
          // 错误时清空输入并重新聚焦
          setTimeout(() => {
            this.clear();
            this.focus();
          }, 100);
        }
      }
    }
  },
  mounted() {
    if (this.autoFocus) {
      this.$nextTick(() => {
        this.focus();
      });
    }
  },
  methods: {
    // 更新显示数组
    updateCodeArray(value) {
      const arr = new Array(this.length).fill('');
      for (let i = 0; i < value.length && i < this.length; i++) {
        arr[i] = value[i];
      }
      this.codeArray = arr;
    },

    // 处理输入
    handleInput(e) {
      let value = e.target.value.replace(/[^\d]/g, ''); // 只保留数字
      if (value.length > this.length) {
        value = value.slice(0, this.length);
      }
      this.inputValue = value;
    },

    // 处理聚焦
    handleFocus() {
      this.isFocused = true;
      this.$emit('focus');
    },

    // 处理失焦
    handleBlur() {
      this.isFocused = false;
      this.$emit('blur');
    },

    // 点击容器聚焦
    focusInput() {
      if (!this.disabled) {
        this.focus();
      }
    },

    // 聚焦输入框
    focus() {
      if (this.$refs.hiddenInput) {
        this.$refs.hiddenInput.focus();
      }
    },

    // 失焦输入框
    blur() {
      if (this.$refs.hiddenInput) {
        this.$refs.hiddenInput.blur();
      }
    },

    // 清空输入
    clear() {
      this.inputValue = '';
    },

    // 获取当前值
    getValue() {
      return this.inputValue;
    },

    // 设置值
    setValue(value) {
      this.inputValue = String(value)
        .replace(/[^\d]/g, '')
        .slice(0, this.length);
    }
  }
};
</script>

<style scoped lang="less">
.verification-code {
  width: 100%;

  &__hidden-input {
    position: absolute;
    left: -9999px;
    opacity: 0;
    pointer-events: none;
  }

  &__container {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 20px;
  }

  &__item {
    position: relative;
    width: 55px;
    height: 55px;
    border: 2px solid #e8eaec;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #fff;
    transition: all 0.3s ease;

    &--active {
      border-color: #5fc5ce;
      box-shadow: 0 0 0 4px rgba(95, 197, 206, 0.1);
    }

    &--filled {
      border-color: #5fc5ce;
      background-color: rgba(95, 197, 206, 0.05);
    }

    &--error {
      border-color: #ff4757;
      background-color: rgba(255, 71, 87, 0.05);
      animation: shake 0.5s ease-in-out;
    }
  }

  &__text {
    font-size: 36px;
    font-weight: 600;
    color: #333;
    line-height: 1;
  }

  &__cursor {
    position: absolute;
    width: 2px;
    height: 40px;
    background-color: #5fc5ce;
    animation: blink 1s infinite;
  }

  &__error {
    margin-top: 20px;
    text-align: center;

    text {
      font-size: 24px;
      color: #ff4757;
    }
  }
}

// 光标闪烁动画
@keyframes blink {
  0%,
  50% {
    opacity: 1;
  }
  51%,
  100% {
    opacity: 0;
  }
}

// 错误抖动动画
@keyframes shake {
  0%,
  100% {
    transform: translateX(0);
  }
  25% {
    transform: translateX(-10px);
  }
  75% {
    transform: translateX(10px);
  }
}

// 深色主题适配
.dark {
  .verification-code {
    &__item {
      background-color: #1a1a1a;
      border-color: #333;

      &--active {
        border-color: #5fc5ce;
      }

      &--filled {
        border-color: #5fc5ce;
        background-color: rgba(95, 197, 206, 0.1);
      }
    }

    &__text {
      color: #fff;
    }
  }
}
</style>
